/* eslint-disable @typescript-eslint/no-inferrable-types */
import {
  IUiAlertContent,
  ToastService,
  TUiAlertTypes,
} from '@irembo-andela/irembogov3-common';
import { ClientService } from './../../../../../core/services/client.service';
import { FormGroup, FormControl, Validators } from '@angular/forms';

import { HttpErrorResponse } from '@angular/common/http';

import {
  Component,
  EventEmitter,
  Output,
  ViewChild,
  ElementRef,
  OnInit,
  OnDestroy,
} from '@angular/core';

import {
  catchError,
  debounceTime,
  distinctUntilChanged,
  EMPTY,
  filter,
  finalize,
  Subject,
  switchMap,
  takeUntil,
} from 'rxjs';
import { emailOrPhoneValidator } from '../../../../../core/utils/forms-utils';
import { UsersService } from '../../../../../core/services/users.service';
import { RequestTypeEnum } from '../../../../../core/models/request-type.enum.model';

@Component({
  selector: 'irembogov-client-manager-inivite-qa-reviewer',
  templateUrl: './client-manager-inivite-qa-reviewer.component.html',
})
export class ClientManagerIniviteQaReviewerComponent
  implements OnInit, OnDestroy
{
  @Output() closeForm: EventEmitter<boolean> = new EventEmitter<boolean>();
  @Output() newQAFormSubmitted: EventEmitter<any> = new EventEmitter<any>();
  @ViewChild('errorMsgDiv') errorMsgDiv!: ElementRef;

  formData!: FormData;
  fileInputsRequired = true;

  isLoading = false;
  showFormError = false;
  formAlertContent: IUiAlertContent = {
    title: '',
    message: '',
    type: 'warning',
  };

  addNewQaReviewer: FormGroup = new FormGroup({
    firstName: new FormControl<string>('', [Validators.required]),
    lastName: new FormControl<string>('', [Validators.required]),
    username: new FormControl<string>('', [
      Validators.required,
      emailOrPhoneValidator(),
    ]),
  });
  isAssigning = false;
  private unsubscribe$ = new Subject<void>();

  constructor(
    private clientService: ClientService,
    private toastService: ToastService,
    private usersService: UsersService
  ) {}

  ngOnInit(): void {
    this.formData = new FormData();
    this.addNewQaReviewer.reset();
    this.initiateUsernameSubscription();
  }

  cancelForm() {
    this.isLoading = false;
    this.closeForm.emit(true);
  }

  initiateUsernameSubscription() {
    this.addNewQaReviewer.controls['username'].valueChanges
      .pipe(
        debounceTime(400),
        distinctUntilChanged(),
        filter(() => {
          if (
            !this.addNewQaReviewer.controls['username'].valid &&
            this.isAssigning
          ) {
            this.addNewQaReviewer.controls['firstName'].reset();
            this.addNewQaReviewer.controls['lastName'].reset();
            this.isAssigning = false;
          }
          return this.addNewQaReviewer.controls['username'].valid;
        }),
        switchMap(value => {
          return this.usersService.checkIfIremboUserExists(value).pipe(
            catchError(() => {
              if (this.isAssigning) {
                this.addNewQaReviewer.controls['firstName'].reset();
                this.addNewQaReviewer.controls['lastName'].reset();
                this.isAssigning = false;
              }
              return EMPTY;
            })
          );
        }),
        takeUntil(this.unsubscribe$)
      )
      .subscribe({
        next: res => {
          this.isAssigning = true;
          this.addNewQaReviewer.controls['firstName'].setValue(
            res.data.firstName
          );
          this.addNewQaReviewer.controls['lastName'].setValue(
            res.data.lastName
          );
        },
      });
  }

  onCreateNewQAReviewer() {
    if (!this.checkIfFormValid(this.addNewQaReviewer)) {
      return;
    }

    this.updateFormErrorMessageContent(false);
    this.isLoading = true;

    const req: any = {
      firstName: this.addNewQaReviewer.controls['firstName'].value,
      lastName: this.addNewQaReviewer.controls['lastName'].value,
      username: this.addNewQaReviewer.controls['username'].value,
      requestType: this.isAssigning
        ? RequestTypeEnum.ASSIGN
        : RequestTypeEnum.CREATE,
    };

    this.clientService
      .initiateRequestToCreateQAReviewer(req)
      .pipe(
        finalize(() => {
          this.isLoading = false;
          this.formData = new FormData();
        })
      )
      .subscribe({
        next: () => {
          const newQaReviewer: any = {
            firstName: req.firstName,
          } as any;

          this.newQAFormSubmitted.emit(newQaReviewer);
          this.toastService.show({
            body: this.isAssigning
              ? 'QA manager role assigned successfully'
              : `Created new QA manager success!`,
            type: 'success',
          });
        },
        error: (error: HttpErrorResponse) => {
          const errorMessage =
            error.error && error.error.message
              ? error.error.message
              : this.isAssigning
              ? 'Failed to assign QA manager role'
              : 'Could not initiate new QA manager';

          this.updateFormErrorMessageContent(
            true,
            'danger',
            this.isAssigning
              ? 'Failed to assign QA manager role'
              : 'Creating new QA manager failed!',
            errorMessage
          );
        },
      });
  }

  updateFormErrorMessageContent(
    show: boolean,
    type: TUiAlertTypes = 'warning',
    title: string = '',
    message: string = ''
  ) {
    this.showFormError = show;
    this.formAlertContent = { type, title, message };
    if (show) {
      this.errorMsgDiv.nativeElement.scrollIntoView({ behavior: 'smooth' });
    }
  }

  private checkIfFormValid(form: FormGroup): boolean {
    if (form.invalid) {
      this.updateFormErrorMessageContent(
        true,
        'danger',
        'Invalid fields!',
        'Please ensure you have filled in all the required fields.'
      );
      return false;
    }
    return true;
  }

  ngOnDestroy(): void {
    this.unsubscribe$.next();
    this.unsubscribe$.complete();
  }
}
